import {
  Button,
  ButtonIcon,
  DrawerBackdrop,
  DrawerShell,
  ShellIconPhone,
  ShellIconWhatsApp,
  ShellIconX,
  useIsPhone,
  type ShellStrokeIconProps,
} from '@zrsystem/ui';
import { useEffect, type ComponentType, type ReactElement } from 'react';

import { useWorkspace } from '@/app/workspace/use-workspace.js';
import {
  useChatSidePanel,
  type ChatSidePanelChannel,
} from '@/features/conversation/chat-side-panel-context.js';
import { StaffChatInboxPresenter } from '@/features/conversation/presenters/staff-chat-inbox.presenter.js';
import { WhatsAppInboxPresenter } from '@/features/notification/presenters/whatsapp-inbox.presenter.js';

import './chat-side-panel.css';

const CHANNELS: readonly {
  readonly id: ChatSidePanelChannel;
  readonly label: string;
  readonly icon: ComponentType<ShellStrokeIconProps>;
}[] = [
  { id: 'workshop', label: 'Sistema', icon: ShellIconPhone },
  { id: 'whatsapp', label: 'WhatsApp', icon: ShellIconWhatsApp },
];

export interface ChatSidePanelProps {
  readonly onNavigateToSettings?: () => void;
}

export function ChatSidePanel({ onNavigateToSettings }: ChatSidePanelProps): ReactElement | null {
  const { open, channel, close, setChannel } = useChatSidePanel();
  const { activePlace } = useWorkspace();
  const isPhone = useIsPhone();
  const placeId = activePlace?.id ?? '';

  useEffect(() => {
    if (!open) {
      return;
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        close();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  if (!open || placeId.length === 0) {
    return null;
  }

  return (
    <DrawerBackdrop
      className="chat-side-panel"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          close();
        }
      }}
    >
      <DrawerShell
        className={
          isPhone
            ? 'chat-side-panel__shell chat-side-panel__shell--phone'
            : 'chat-side-panel__shell'
        }
        aria-label="Conversas"
      >
        <header className="chat-side-panel__header">
          <div className="chat-side-panel__switch" role="tablist" aria-label="Canal">
            {CHANNELS.map((item) => {
              const Icon = item.icon;
              const selected = channel === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  className={
                    selected
                      ? 'chat-side-panel__switch-item chat-side-panel__switch-item--active'
                      : 'chat-side-panel__switch-item'
                  }
                  onClick={() => {
                    setChannel(item.id);
                  }}
                >
                  <Icon size={16} />
                  {item.label}
                </button>
              );
            })}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            iconOnly
            aria-label="Fechar conversas"
            onClick={close}
          >
            <ButtonIcon>
              <ShellIconX size={16} />
            </ButtonIcon>
          </Button>
        </header>
        <div className="chat-side-panel__body">
          {channel === 'workshop' ? <StaffChatInboxPresenter stacked /> : null}
          {channel === 'whatsapp' ? (
            <WhatsAppInboxPresenter
              placeId={placeId}
              stacked
              {...(onNavigateToSettings === undefined ? {} : { onNavigateToSettings })}
            />
          ) : null}
        </div>
      </DrawerShell>
    </DrawerBackdrop>
  );
}
